import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import PublicIcon from "@mui/icons-material/Public";
import { ListQuery } from "../../../Types/CommonTypes";
import { CountryViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../../../components/ResponsivePagination";
const CountryList: React.FC = () => {
    const { t } = useTranslation();
    const [viewModel, setViewModel] = useState<CountryViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
    });
    const [filter, setFilter] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: CountryViewModel[]; count: number }>(
                "/settingsApi/GetCountryList",
                query
            );
            setViewModel(response.items);
            setTotalCount(response.count);
        } catch (error : any) {
            showSnackbar(`${t("common.failedToFetchEntries")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (code?: string | null) => {
        navigate({ path: "/settings/country/edit", params: { code } });
    };

    const handleCreate = () => {
        navigate({ path: "/settings/country/edit", params: { code: "" } });
    };

    return (
        <CenteredListCard title={t("common.country")} >
 
            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreate}
                >
                        {t("common.create")}
                </Button>
            </Box>
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <TextField
                        label={t("common.search")}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => setFilter(event.target.value)}
                    value={filter}
                    disabled={loading}
                />
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                >
                        {t("common.search")}
                </Button>
            </Box>


            <Grid container spacing={2} sx={{ mb: 2 }}>
                {viewModel.map((item) => (
                    <Grid item
                        xs={12}
                        sm={viewModel.length === 1 ? 12 : viewModel.length === 2 ? 6 : 6}
                        md={viewModel.length === 1 ? 12 : viewModel.length === 2 ? 6 : 4}
                        key={item.code}>
                        <Card
                            onClick={() => handleSelect(item.code)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<PublicIcon fontSize="small" color="action" />}
                                    title={item.name}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.code")}: {item.code}
                                </Typography>
                            
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {!loading && viewModel.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalCount}

                onPageChange={handlePageChange}

            />
      
        </CenteredListCard>

    );
};

export default CountryList;
